import { SliceCreator } from "@/features/vidyo-call/types/vidyo-common";

import { LocalCamera } from "../types/vidyo-local-camera";
import { LocalMicrophone } from "../types/vidyo-local-microphone";
import { LocalSpeaker } from "../types/vidyo-local-speaker";

export interface VidyoDeviceState {
  camera: {
    /**
     * The available cameras.
     * We add these in the local-devices-lifecycle.tsx
     */
    available: LocalCamera[];
    /**
     * The currently selected camera,
     * selected by the user in the UI.
     * Or automatically selected if its the preferred camera.
     */
    selected: LocalCamera | undefined;
    /**
     * The preferred camera id.
     * We persist this in the local storage so that we can restore the preferred camera
     */
    preferred: string | undefined;
    /**
     * User selected to mute the camera.
     */
    shouldBeMuted: boolean;
    /**
     * Server muted the camera.
     */
    serverMuted: boolean;
    /**
     * Whether the camera has permission to be used.
     */
    hasPermission: boolean;
    add: (camera: LocalCamera) => void;
    remove: (camera: LocalCamera) => void;
    select: (camera: LocalCamera) => void;
    setPreferred: (camera: LocalCamera) => void;
    toggle: () => void;
    setServerMute: (shouldBeMuted: boolean) => void;
    mute: () => void;
    setHasPermission: (hasPermission: boolean) => void;
  };
  microphone: {
    /**
     * The available microphones.
     * We add these in the local-devices-lifecycle.tsx
     */
    available: LocalMicrophone[];
    /**
     * The currently selected microphone,
     * selected by the user in the UI.
     * Or automatically selected if its the preferred microphone.
     */
    selected: LocalMicrophone | undefined;
    /**
     * The preferred microphone id.
     * We persist this in the local storage so that we can restore the preferred microphone
     */
    preferred: string | undefined;
    /**
     * User selected to mute the microphone.
     */
    shouldBeMuted: boolean;
    /**
     * Server muted the microphone.
     */
    serverMuted: boolean;
    /**
     * Whether the microphone has permission to be used.
     */
    hasPermission: boolean;
    add: (microphone: LocalMicrophone) => void;
    remove: (microphone: LocalMicrophone) => void;
    select: (microphone: LocalMicrophone) => void;
    setPreferred: (microphone: LocalMicrophone) => void;
    toggle: () => void;
    setServerMute: (shouldBeMuted: boolean) => void;
    mute: () => void;
    setHasPermission: (hasPermission: boolean) => void;
  };

  speaker: {
    /**
     * The available speakers.
     * We add these in the local-devices-lifecycle.tsx
     */
    available: LocalSpeaker[];
    /**
     * The currently selected speaker,
     * selected by the user in the UI.
     * Or automatically selected if its the preferred speaker.
     */
    selected: LocalSpeaker | undefined;
    /**
     * The preferred speaker id.
     * We persist this in the local storage so that we can restore the preferred speaker
     */
    preferred: string | undefined;
    /**
     * User selected to mute the speaker.
     */
    shouldBeMuted: boolean;
    /**
     * Server muted the speaker.
     */
    serverMuted: boolean;
    add: (speaker: LocalSpeaker) => void;
    remove: (speaker: LocalSpeaker) => void;
    select: (speaker: LocalSpeaker) => void;
    setPreferred: (speaker: LocalSpeaker) => void;
    toggle: () => void;
  };
}

export const createVidyoDeviceSlice: SliceCreator<keyof VidyoDeviceState> = (
  set,
) => ({
  camera: {
    available: [],
    selected: undefined,
    preferred: undefined,
    shouldBeMuted: process.env.NODE_ENV === "development",
    serverMuted: false,
    hasPermission: true,
    add: async (camera: LocalCamera) => {
      set(
        (state) => {
          state.camera.available.push(camera);
        },
        undefined,
        "camera/add",
      );
    },
    remove: (camera: LocalCamera) =>
      set(
        (state) => {
          state.camera.available = state.camera.available.filter(
            (c) => c.id !== camera.id,
          );

          if (state.camera.selected?.id === camera.id) {
            state.camera.selected = undefined;
          }
        },
        undefined,
        "camera/remove",
      ),
    select: (camera: LocalCamera) => {
      set(
        (state) => {
          state.camera.selected = camera;
        },
        undefined,
        "camera/select",
      );
    },
    toggle: () =>
      set(
        (state) => {
          state.camera.shouldBeMuted = !state.camera.shouldBeMuted;
        },
        undefined,
        "camera/toggle",
      ),
    setServerMute: (shouldBeMuted: boolean) =>
      set(
        (state) => {
          state.camera.serverMuted = shouldBeMuted;
          // even if we "server-unmute" the user, we dont want to automatically enable camera
          state.camera.shouldBeMuted = true;
        },
        undefined,
        "camera/setServerMute",
      ),
    mute: () =>
      set(
        (state) => {
          state.camera.shouldBeMuted = true;
        },
        undefined,
        "camera/mute",
      ),
    setPreferred: (camera: LocalCamera) =>
      set(
        (state) => {
          state.camera.preferred = camera.id;
        },
        undefined,
        "camera/setPreferred",
      ),
    setHasPermission: (hasPermission: boolean) =>
      set(
        (state) => {
          state.camera.hasPermission = hasPermission;
        },
        undefined,
        "camera/setHasPermission",
      ),
  },

  microphone: {
    available: [],
    selected: undefined,
    preferred: undefined,
    shouldBeMuted: process.env.NODE_ENV === "development",
    serverMuted: false,
    hasPermission: true,
    add: async (microphone: LocalMicrophone) => {
      set(
        (state) => {
          state.microphone.available.push(microphone);
        },
        undefined,
        "microphone/add",
      );
    },
    remove: (microphone: LocalMicrophone) =>
      set(
        (state) => {
          state.microphone.available = state.microphone.available.filter(
            (m) => m.id !== microphone.id,
          );
        },
        undefined,
        "microphone/remove",
      ),
    select: (microphone: LocalMicrophone) => {
      set(
        (state) => {
          state.microphone.selected = microphone;
        },
        undefined,
        "microphone/select",
      );
    },
    toggle: () =>
      set(
        (state) => {
          state.microphone.shouldBeMuted = !state.microphone.shouldBeMuted;
        },
        undefined,
        "microphone/toggle",
      ),
    setServerMute: (shouldBeMuted: boolean) =>
      set(
        (state) => {
          state.microphone.serverMuted = shouldBeMuted;
          // even if we "server-unmute" the user, we dont want to automatically enable mic
          state.microphone.shouldBeMuted = true;
        },
        undefined,
        "microphone/setServerMute",
      ),
    setPreferred: (microphone: LocalMicrophone) =>
      set(
        (state) => {
          state.microphone.preferred = microphone.id;
        },
        undefined,
        "microphone/setPreferred",
      ),
    mute: () =>
      set(
        (state) => {
          state.microphone.shouldBeMuted = true;
        },
        undefined,
        "microphone/mute",
      ),
    setHasPermission: (hasPermission: boolean) =>
      set(
        (state) => {
          state.microphone.hasPermission = hasPermission;
        },
        undefined,
        "microphone/setHasPermission",
      ),
  },

  speaker: {
    available: [],
    selected: undefined,
    preferred: undefined,
    shouldBeMuted: false,
    serverMuted: false,
    add: async (speaker: LocalSpeaker) => {
      set(
        (state) => {
          state.speaker.available.push(speaker);
        },
        undefined,
        "speaker/add",
      );
    },
    remove: (speaker: LocalSpeaker) =>
      set(
        (state) => {
          state.speaker.available = state.speaker.available.filter(
            (s) => s.id !== speaker.id,
          );
        },
        undefined,
        "microphone/remove",
      ),
    select: (speaker: LocalSpeaker) => {
      set(
        (state) => {
          state.speaker.selected = speaker;
        },
        undefined,
        "speaker/select",
      );
    },
    toggle: () =>
      set(
        (state) => {
          state.speaker.shouldBeMuted = !state.speaker.shouldBeMuted;
        },
        undefined,
        "speaker/toggle",
      ),
    setPreferred: (speaker: LocalSpeaker) =>
      set(
        (state) => {
          state.speaker.preferred = speaker.id;
        },
        undefined,
        "speaker/setPreferred",
      ),
  },
});
